/* =========================================================
   GM Banner Slider — Gud Mishri
   assets/gm-banner-hub.js
   - Slider: slide / fade, arrows, dots with progress, autoplay,
     pause on hover / pause button, swipe + mouse drag, keyboard
   - Text animations replay on every slide change
   - Only the active slide's video plays; next slide pre-loads
   - Product tabs (click + arrow keys)
   - Shopify theme editor support
   ========================================================= */
(function () {
  if (window.GMBannerSlider) return;
  window.GMBannerSlider = true;

  document.documentElement.classList.add('gm-js');

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mqMobile = window.matchMedia('(max-width: 749px)');
  var TYPE_SPEED = 55;
  var SWIPE_MIN = 50;

  /* =======================================================
     Text animation helpers
     ======================================================= */
  function splitWords(el) {
    if (el.dataset.gmSplit) return;
    el.dataset.gmSplit = '1';
    var text = el.textContent.trim();
    var words = text.split(/\s+/);
    el.setAttribute('aria-label', text);
    el.textContent = '';
    words.forEach(function (w, i) {
      var mask = document.createElement('span');
      mask.className = 'gm-word-mask';
      mask.setAttribute('aria-hidden', 'true');
      var word = document.createElement('span');
      word.className = 'gm-word';
      word.style.setProperty('--w', i);
      word.textContent = w;
      mask.appendChild(word);
      el.appendChild(mask);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
  }

  function prepType(el, node) {
    if (el.dataset.gmFull !== undefined) return;
    var full = el.textContent.trim();
    el.dataset.gmFull = full;
    el.setAttribute('aria-label', full);
    el.textContent = ' ';
    node.style.setProperty('--gm-type-ms', Math.min(full.length * TYPE_SPEED, 2500) + 'ms');
  }

  function runType(el) {
    var full = el.dataset.gmFull || '';
    var runId = (el._gmRun || 0) + 1;
    el._gmRun = runId;
    var i = 0;
    el.classList.add('gm-typing');
    setTimeout(function tick() {
      if (el._gmRun !== runId) return;
      i++;
      el.textContent = full.slice(0, i) || ' ';
      if (i < full.length) setTimeout(tick, TYPE_SPEED);
      else el.classList.remove('gm-typing');
    }, 300);
  }

  function resetType(el) {
    el._gmRun = (el._gmRun || 0) + 1;
    el.classList.remove('gm-typing');
    el.textContent = ' ';
  }

  function prepAnimations(scope) {
    scope.querySelectorAll('[data-gm-animate]').forEach(function (node) {
      var type = node.dataset.gmAnimate;
      if (reduceMotion || type === 'none') return;
      if (type === 'word-reveal') node.querySelectorAll('.gm-bh__heading').forEach(splitWords);
      if (type === 'typewriter') node.querySelectorAll('.gm-bh__heading').forEach(function (h) { prepType(h, node); });
    });
  }

  function revealSlide(slide) {
    slide.querySelectorAll('[data-gm-animate]').forEach(function (node) {
      node.classList.remove('is-resetting');
      if (reduceMotion || node.dataset.gmAnimate === 'none') { node.classList.add('is-visible'); return; }
      // next frame so the browser registers the hidden state first
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          node.classList.add('is-visible');
          if (node.dataset.gmAnimate === 'typewriter') node.querySelectorAll('.gm-bh__heading').forEach(runType);
        });
      });
    });
  }

  function hideSlideText(slide) {
    slide.querySelectorAll('[data-gm-animate]').forEach(function (node) {
      if (reduceMotion || node.dataset.gmAnimate === 'none') return;
      node.classList.add('is-resetting');
      node.classList.remove('is-visible');
      if (node.dataset.gmAnimate === 'typewriter') node.querySelectorAll('.gm-bh__heading').forEach(resetType);
      void node.offsetWidth;
      node.classList.remove('is-resetting');
    });
  }

  /* =======================================================
     Media helpers
     ======================================================= */
  function loadSlide(slide) {
    if (!slide) return;
    slide.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    slide.querySelectorAll('video[preload="none"]').forEach(function (v) { v.preload = 'metadata'; });
  }
  function playSlide(slide) {
    loadSlide(slide);
    slide.querySelectorAll('iframe[data-src]').forEach(function (f) {
      f.src = f.dataset.src;
      f.removeAttribute('data-src');
    });
    slide.querySelectorAll('video').forEach(function (v) {
      v.muted = true;
      v.setAttribute('playsinline', '');
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }
  function pauseSlide(slide) {
    slide.querySelectorAll('video').forEach(function (v) { v.pause(); });
  }


  /* =======================================================
     Video + product tabs: fit products inside the banner
     (the panel never scrolls, so the page scroll never gets stuck)
     ======================================================= */
  function fitVp(panel) {
    if (!panel) return;
    var vp = panel.closest('.gm-bh__vp');
    if (!vp) return;
    vp.style.removeProperty('--vp-card-w-fit');
    vp.querySelectorAll('.gm-fit-hide').forEach(function (c) { c.classList.remove('gm-fit-hide'); });
    if (vp.dataset.autofit === 'false' || !panel.clientHeight || !panel.clientWidth) return;

    var grid = panel.querySelector('.gm-bh__panel:not([hidden]) .gm-bh__grid');
    if (!grid) return;

    var cs = getComputedStyle(panel);
    var availH = panel.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    var innerW = panel.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    var mobile = mqMobile.matches;
    var minW = mobile ? 84 : 110;

    function contentHeight() {
      var top = Infinity, bottom = -Infinity;
      Array.prototype.forEach.call(panel.children, function (ch) {
        if (ch.hidden || !ch.offsetHeight) return;
        var r = ch.getBoundingClientRect();
        var m = getComputedStyle(ch);
        top = Math.min(top, r.top - parseFloat(m.marginTop));
        bottom = Math.max(bottom, r.bottom + parseFloat(m.marginBottom));
      });
      return isFinite(top) ? bottom - top : 0;
    }
    function fits() { return contentHeight() <= availH + 1; }
    function setW(w) { vp.style.setProperty('--vp-card-w-fit', Math.floor(w) + 'px'); }
    function visibleCards() {
      return Array.prototype.filter.call(grid.children, function (c) { return c.offsetHeight > 0 && c.classList.contains('gm-bh__card'); });
    }

    // biggest card the panel width allows (one column each), capped by "Max product card width"
    var gcs = getComputedStyle(grid);
    var cols = gcs.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    var visible = visibleCards().length || cols;
    cols = Math.min(cols, visible);                 // fewer products than columns: let them grow
    var gap = parseFloat(gcs.columnGap) || 0;
    var maxW = (innerW - gap * (cols - 1)) / cols;
    if (!mobile) {
      var cap = parseFloat(getComputedStyle(vp).getPropertyValue('--vp-card-w'));
      if (cap > 0) maxW = Math.min(maxW, cap);
    }
    maxW = Math.max(minW, maxW);

    // 1) grow to fill, or shrink until the height fits (binary search)
    setW(maxW);
    if (!fits()) {
      var lo = minW, hi = maxW;
      for (var i = 0; i < 9; i++) {
        var mid = (lo + hi) / 2;
        setW(mid);
        if (fits()) lo = mid; else hi = mid;
      }
      setW(lo);
    }

    // 2) still too many products even at the smallest size: hide the last row(s)
    var guard = 0;
    while (!fits() && guard++ < 12) {
      var vis = visibleCards();
      var c = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      if (vis.length <= c) break;
      var remove = vis.length % c || c;
      vis.slice(-remove).forEach(function (el) { el.classList.add('gm-fit-hide'); });
    }
  }

  function initVpFit(root) {
    root.querySelectorAll('.gm-bh__vp-panel:not([data-gm-fit])').forEach(function (panel) {
      panel.setAttribute('data-gm-fit', '');
      var run = function () { fitVp(panel); };
      if ('ResizeObserver' in window) {
        var last = 0;
        new ResizeObserver(function (entries) {
          // only refit when the panel box itself changes size
          var h = Math.round(entries[0].contentRect.height) + 'x' + Math.round(entries[0].contentRect.width);
          if (h !== last) { last = h; run(); }
        }).observe(panel);
      }
      window.addEventListener('resize', run);
      window.addEventListener('load', run);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      run();
    });
  }

  /* =======================================================
     Product tabs
     ======================================================= */
  function initTabs(root, onInteract) {
    root.querySelectorAll('[data-gm-tabs]:not([data-gm-tabs-init])').forEach(function (wrap) {
      wrap.setAttribute('data-gm-tabs-init', '');
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));

      function activate(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus({ preventScroll: true });
        fitVp(wrap);
        if (onInteract) onInteract();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(tab, false); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') next = tabs[0];
          if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) { e.preventDefault(); e.stopPropagation(); activate(next, true); }
        });
      });
    });
  }


  /* =======================================================
     Floating product cards (rotate every few seconds)
     ======================================================= */
  function Rotator(el) {
    this.el = el;
    this.items = Array.prototype.slice.call(el.querySelectorAll('[data-gm-fc-item]'));
    this.dotsWrap = el.querySelector('[data-gm-fc-dots]');
    this.speed = parseInt(el.dataset.speed, 10) || 4000;
    this.perDesktop = Math.max(1, parseInt(el.dataset.per, 10) || 1);
    this.page = 0;
    this.active = false;
    this.hold = false;
    this.timer = null;
    var self = this;

    var prev = el.querySelector('[data-gm-fc-prev]');
    var next = el.querySelector('[data-gm-fc-next]');
    if (prev) prev.addEventListener('click', function (e) { e.stopPropagation(); self.show(self.page - 1); });
    if (next) next.addEventListener('click', function (e) { e.stopPropagation(); self.show(self.page + 1); });
    el.addEventListener('mouseenter', function () { self.setHold(true); });
    el.addEventListener('mouseleave', function () { self.setHold(false); });
    el.addEventListener('focusin', function () { self.setHold(true); });
    el.addEventListener('focusout', function () { self.setHold(false); });

    var refit = function () { self.fit(); };
    if ('ResizeObserver' in window && el.parentElement) {
      this.ro = new ResizeObserver(refit);
      this.ro.observe(el.parentElement);
    }
    window.addEventListener('resize', refit);
    window.addEventListener('load', refit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
    el.querySelectorAll('img').forEach(function (img) { if (!img.complete) img.addEventListener('load', refit, { once: true }); });

    this.layout();
  }
  Rotator.prototype.per = function () { return mqMobile.matches ? 1 : this.perDesktop; };
  Rotator.prototype.pages = function () { return Math.max(1, Math.ceil(this.items.length / this.per())); };
  Rotator.prototype.layout = function () {
    var self = this;
    if (this.dotsWrap) {
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (var i = 0; i < this.pages(); i++) {
        var d = document.createElement('span');
        d.className = 'gm-fc__dot';
        this.dotsWrap.appendChild(d);
        this.dots.push(d);
      }
    }
    var nav = this.el.querySelector('.gm-fc__nav');
    if (nav) nav.hidden = this.pages() < 2;
    this.show(Math.min(this.page, this.pages() - 1), true);
  };
  Rotator.prototype.show = function (p, silent) {
    var n = this.pages();
    p = (p % n + n) % n;
    this.page = p;
    var per = this.per();
    var k = 0;
    this.items.forEach(function (it, i) {
      var on = Math.floor(i / per) === p;
      if (on) {
        it.classList.remove('is-active');
        void it.offsetWidth; // replay entrance animation
        it.style.setProperty('--i', k++);
        it.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
      }
      it.classList.toggle('is-active', on);
      it.inert = !on;
    });
    if (this.dots) this.dots.forEach(function (d, i) { d.classList.toggle('is-active', i === p); });
    this.fit();
    this.restart();
  };
  /* Places the card(s) exactly where the position setting says and
     keeps them fully inside the banner (never cut, never under the
     slider arrows / dots). Same card design is kept: if space is short
     the card only gets narrower. */
  var GM_ANCHORS = {
    'top-left': [0, 0], 'top-center': [.5, 0], 'top-right': [1, 0],
    'middle-left': [0, .5], 'center': [.5, .5], 'middle-right': [1, .5],
    'bottom-left': [0, 1], 'bottom-center': [.5, 1], 'bottom-right': [1, 1],
    'top': [.5, 0], 'middle': [.5, .5], 'bottom': [.5, 1]
  };
  Rotator.prototype.anchor = function (mobile) {
    var d = this.el.dataset;
    if (mobile) {
      if (d.mpos === 'custom') {
        var mx = parseFloat(d.mx), my = parseFloat(d.my);
        return [isNaN(mx) ? 1 : mx / 100, isNaN(my) ? 1 : my / 100];
      }
      return GM_ANCHORS[d.mpos] || [.5, 1];
    }
    if (d.pos === 'custom') {
      var x = parseFloat(d.x), y = parseFloat(d.y);
      return [isNaN(x) ? .75 : x / 100, isNaN(y) ? .6 : y / 100];
    }
    return GM_ANCHORS[d.pos] || [1, 1];
  };
  Rotator.prototype.fit = function () {
    var el = this.el;
    var box = el.parentElement;
    if (!box) return;
    var b = box.getBoundingClientRect();
    if (!b.width || !b.height) return;
    var mobile = mqMobile.matches;
    if (mobile && el.dataset.mpos === 'hidden') return;
    var hasArrows = !!el.closest('[data-gm-slider]') && !!el.closest('[data-gm-slider]').querySelector('.gm-bs__arrow');
    var mLand = mobile && !!el.closest('.gm-bs--mland');
    var padX = mobile ? (mLand ? 8 : 12) : (hasArrows ? 76 : 20);
    var padT = mobile ? (mLand ? 6 : 12) : 16;
    var padB = mobile ? (mLand ? 28 : 58) : 60;

    // reset to the CSS state
    el.classList.remove('is-squeezed', 'is-placed');
    el.style.removeProperty('--fc-img-h');
    el.style.removeProperty('--fc-w-fit');
    el.style.removeProperty('left');
    el.style.removeProperty('top');

    var parts = [el.querySelector('.gm-fc__stack'), el.querySelector('.gm-fc__nav:not([hidden])')].filter(Boolean);
    function union() {
      var r = { top: Infinity, left: Infinity, right: -Infinity, bottom: -Infinity };
      parts.forEach(function (n) {
        var q = n.getBoundingClientRect();
        if (!q.width && !q.height) return;
        r.top = Math.min(r.top, q.top); r.left = Math.min(r.left, q.left);
        r.right = Math.max(r.right, q.right); r.bottom = Math.max(r.bottom, q.bottom);
      });
      return r;
    }
    var u = union();
    if (!isFinite(u.top)) return;

    var availW = b.width - padX * 2;
    var availH = b.height - padT - padB;
    var item = el.querySelector('.gm-fc__item.is-active');
    var count = el.querySelectorAll('.gm-fc__item.is-active').length || 1;

    // too wide -> narrower cards
    var overW = (u.right - u.left) - availW;
    if (overW > 0 && item) {
      el.style.setProperty('--fc-w-fit', Math.max(130, item.offsetWidth - Math.ceil(overW / count) - 2) + 'px');
      u = union();
    }
    // too tall -> narrower card (square image shrinks with it, design stays the same)
    var overH = (u.bottom - u.top) - availH;
    var horizontal = el.classList.contains(mobile ? 'gm-fc--m-horizontal' : 'gm-fc--horizontal');
    if (overH > 0 && item && !horizontal) {
      el.style.setProperty('--fc-w-fit', Math.max(mLand ? 96 : 130, item.offsetWidth - overH - 2) + 'px');
      u = union();
      overH = (u.bottom - u.top) - availH;
    }
    // still too tall (very short banner) -> shorter image
    if (overH > 0) {
      var img = el.querySelector('.gm-fc__item.is-active .gm-fc__img');
      if (img) {
        el.style.setProperty('--fc-img-h', Math.max(mLand ? 30 : 44, img.offsetHeight - overH) + 'px');
        el.classList.add('is-squeezed');
        u = union();
      }
    }

    // exact position from the setting
    var a = this.anchor(mobile);
    var cw = u.right - u.left, ch = u.bottom - u.top;
    var targetX = padX + Math.max(0, availW - cw) * a[0];
    var targetY = padT + Math.max(0, availH - ch) * a[1];
    var er = el.getBoundingClientRect();
    var offX = u.left - er.left, offY = u.top - er.top;
    el.classList.add('is-placed');
    el.style.setProperty('left', Math.round(targetX - offX) + 'px', 'important');
    el.style.setProperty('top', Math.round(targetY - offY) + 'px', 'important');

    // correct any remaining offset (e.g. theme CSS on the wrapper)
    var u2 = union();
    var cx = (b.left + targetX) - u2.left, cy = (b.top + targetY) - u2.top;
    if (Math.abs(cx) > 1 || Math.abs(cy) > 1) {
      el.style.setProperty('left', Math.round(targetX - offX + cx) + 'px', 'important');
      el.style.setProperty('top', Math.round(targetY - offY + cy) + 'px', 'important');
    }
  };
  Rotator.prototype.restart = function () {
    clearTimeout(this.timer);
    this.timer = null;
    this.el.classList.remove('is-running');
    if (!this.active || this.pages() < 2 || reduceMotion) return;
    void this.el.offsetWidth;
    this.el.classList.add('is-running');
    this.left = this.speed;
    if (this.hold) return;
    this.startedAt = Date.now();
    this.timer = setTimeout(this.show.bind(this, this.page + 1), this.left);
  };
  Rotator.prototype.setHold = function (on) {
    if (this.hold === on) return;
    this.hold = on;
    this.el.classList.toggle('is-hold', on);
    if (!this.el.classList.contains('is-running')) return;
    if (on && this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      this.left = Math.max(0, this.left - (Date.now() - this.startedAt));
    } else if (!on && !this.timer) {
      this.startedAt = Date.now();
      this.timer = setTimeout(this.show.bind(this, this.page + 1), this.left);
    }
  };
  Rotator.prototype.setActive = function (on) {
    if (this.active === on) return;
    this.active = on;
    this.restart();
  };

  /* ---------- add to cart from a floating card ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-gm-add]');
    if (!btn) return;
    e.preventDefault();
    var label = btn.textContent;
    btn.classList.add('is-loading');
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.gmAdd, 10), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (data) {
        btn.classList.remove('is-loading');
        btn.textContent = 'Added ✓';
        setTimeout(function () { btn.textContent = label; }, 2200);
        // Impulse / common theme cart-drawer events
        var item = data && data.items ? data.items[0] : data;
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item, addToCartBtn: btn } }));
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        window.location.href = root + 'cart';
      });
  });


  /* =======================================================
     Shop the look — hotspots
     Positions are % of the IMAGE, so they stay on the right
     spot even when the image is cropped to fit the slider.
     ======================================================= */
  function Lookbook(el, onOpen) {
    this.el = el;
    this.media = el.querySelector('[data-gm-lb-media]');
    this.spots = Array.prototype.slice.call(el.querySelectorAll('[data-gm-spot]'));
    this.look = el.querySelector('[data-gm-look]');
    this.lookBtn = el.querySelector('[data-gm-look-toggle]');
    this.hover = el.dataset.open === 'hover';
    this.onOpen = onOpen || function () {};
    var self = this;

    this.spots.forEach(function (spot) {
      var dot = spot.querySelector('[data-gm-dot]');
      var card = spot.querySelector('[data-gm-card]');
      card.inert = true;
      dot.addEventListener('click', function (e) {
        e.stopPropagation();
        if (spot.classList.contains('is-open')) self.close(spot);
        else self.open(spot);
      });
      var closeBtn = spot.querySelector('[data-gm-close]');
      if (closeBtn) closeBtn.addEventListener('click', function (e) { e.stopPropagation(); self.close(spot, true); });

      if (self.hover) {
        var t;
        var enter = function () { if (mqMobile.matches) return; clearTimeout(t); self.open(spot); };
        var leave = function () { if (mqMobile.matches) return; t = setTimeout(function () { self.close(spot); }, 220); };
        dot.addEventListener('mouseenter', enter);
        dot.addEventListener('mouseleave', leave);
        card.addEventListener('mouseenter', enter);
        card.addEventListener('mouseleave', leave);
      }
    });

    if (this.lookBtn) {
      this.lookBtn.addEventListener('click', function (e) { e.stopPropagation(); self.toggleLook(); });
      var lc = el.querySelector('[data-gm-look-close]');
      if (lc) lc.addEventListener('click', function (e) { e.stopPropagation(); self.toggleLook(false); });
      this.look.inert = true;
    }

    this.onDocClick = function (e) {
      if (e.target.closest && e.target.closest('.gm-lb__card, .gm-lb__look, .gm-lb__dot, .gm-lb__look-btn')) {
        // clicks inside a card / list keep it open, except on another lookbook
        if (self.el.contains(e.target)) return;
      }
      self.closeAll();
    };
    document.addEventListener('click', this.onDocClick);
    this.onKey = function (e) { if (e.key === 'Escape') self.closeAll(true); };
    el.addEventListener('keydown', this.onKey);

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () { self.layout(); });
      this.ro.observe(el);
    } else {
      window.addEventListener('resize', function () { self.layout(); });
    }
    this.layout();
  }

  Lookbook.prototype.layout = function () {
    var box = this.el.getBoundingClientRect();
    var cw = box.width, ch = box.height;
    if (!cw || !ch) return;
    var mobileImg = mqMobile.matches && this.media.dataset.mw;
    var iw = parseFloat(mobileImg ? this.media.dataset.mw : this.media.dataset.w) || cw;
    var ih = parseFloat(mobileImg ? this.media.dataset.mh : this.media.dataset.h) || ch;
    // object-fit: cover math (centered)
    var scale = Math.max(cw / iw, ch / ih);
    var dw = iw * scale, dh = ih * scale;
    var ox = (cw - dw) / 2, oy = (ch - dh) / 2;
    var useMobile = !!mobileImg;

    this.spots.forEach(function (spot) {
      var x = parseFloat(useMobile ? spot.dataset.mx : spot.dataset.x);
      var y = parseFloat(useMobile ? spot.dataset.my : spot.dataset.y);
      if (isNaN(x)) x = 50;
      if (isNaN(y)) y = 50;
      var px = ox + dw * x / 100;
      var py = oy + dh * y / 100;
      var off = px < 6 || px > cw - 6 || py < 6 || py > ch - 6;
      spot.classList.toggle('is-offscreen', off);
      spot.style.setProperty('--px', px + 'px');
      spot.style.setProperty('--py', py + 'px');

      var card = spot.querySelector('[data-gm-card]');
      card.classList.toggle('is-left', px > cw * 0.55);
      var chh = card.offsetHeight || 320;
      var cy = Math.max(12, Math.min(ch - chh - 12, py - chh / 2));
      spot.style.setProperty('--cy', cy + 'px');
    });
  };

  Lookbook.prototype.open = function (spot) {
    var self = this;
    this.spots.forEach(function (s) { if (s !== spot) self.close(s); });
    this.toggleLook(false);
    spot.classList.add('is-open');
    spot.querySelector('[data-gm-dot]').setAttribute('aria-expanded', 'true');
    var card = spot.querySelector('[data-gm-card]');
    card.inert = false;
    card.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    this.layout();
    this.onOpen();
  };
  Lookbook.prototype.close = function (spot, focusDot) {
    if (!spot.classList.contains('is-open')) return;
    spot.classList.remove('is-open');
    var dot = spot.querySelector('[data-gm-dot]');
    dot.setAttribute('aria-expanded', 'false');
    spot.querySelector('[data-gm-card]').inert = true;
    if (focusDot) dot.focus({ preventScroll: true });
  };
  Lookbook.prototype.closeAll = function (focus) {
    var self = this;
    this.spots.forEach(function (s) { self.close(s, focus && s.classList.contains('is-open')); });
    this.toggleLook(false);
  };
  Lookbook.prototype.toggleLook = function (force) {
    if (!this.look) return;
    var on = typeof force === 'boolean' ? force : !this.el.classList.contains('is-look-open');
    if (on === this.el.classList.contains('is-look-open')) return;
    this.el.classList.toggle('is-look-open', on);
    var sl = this.el.closest('[data-gm-slider]');
    if (sl) sl.classList.toggle('has-look-open', on);
    this.lookBtn.setAttribute('aria-expanded', on ? 'true' : 'false');
    this.look.inert = !on;
    if (on) {
      var self = this;
      this.spots.forEach(function (s) { self.close(s); });
      this.look.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
      this.onOpen();
    }
  };
  Lookbook.prototype.destroy = function () {
    document.removeEventListener('click', this.onDocClick);
    if (this.ro) this.ro.disconnect();
  };

  /* =======================================================
     Slider
     ======================================================= */
  function Slider(root) {
    this.root = root;
    this.viewport = root.querySelector('.gm-bs__viewport');
    this.track = root.querySelector('.gm-bs__track');
    this.allSlides = Array.prototype.slice.call(root.querySelectorAll('[data-gm-slide]'));
    this.dotsWrap = root.querySelector('[data-gm-dots]');
    this.prevBtn = root.querySelector('[data-gm-prev]');
    this.nextBtn = root.querySelector('[data-gm-next]');
    this.pauseBtn = root.querySelector('[data-gm-pause]');

    this.fade = root.dataset.transition === 'fade';
    this.loop = root.dataset.loop !== 'false';
    this.autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
    this.speed = parseInt(root.dataset.speed, 10) || 6000;
    this.pauseHover = root.dataset.pauseHover !== 'false';

    this.index = 0;
    this.timer = null;
    this.remaining = this.speed;
    this.inView = false;
    this.hoverPaused = false;
    this.userPaused = false;
    this.editorPaused = false;
    this.resetTimers = [];

    prepAnimations(root);
    initTabs(root, this.pauseByUser.bind(this));
    initVpFit(root);
    this.amb = root.querySelectorAll('.gm-bs__amb');
    this.ambIdx = 0;
    this.ambSrc = '';
    this.rotators = [];
    var self0 = this;
    root.querySelectorAll('[data-gm-rotator]').forEach(function (el) {
      var r = new Rotator(el);
      r.slide = el.closest('[data-gm-slide]');
      self0.rotators.push(r);
    });
    this.lookbooks = [];
    root.querySelectorAll('[data-gm-lookbook]').forEach(function (el) {
      self0.lookbooks.push(new Lookbook(el, self0.pauseByUser.bind(self0)));
    });
    this.bind();
    this.refresh(true);
    root._gmSlider = this;
  }

  Slider.prototype.visibleSlides = function () {
    return this.allSlides.filter(function (s) { return getComputedStyle(s).display !== 'none'; });
  };

  Slider.prototype.refresh = function (first) {
    var current = this.slides ? this.slides[this.index] : null;
    this.slides = this.visibleSlides();
    var idx = current ? this.slides.indexOf(current) : 0;
    this.index = idx < 0 ? 0 : idx;
    this.root.classList.toggle('is-single', this.slides.length < 2);
    this.buildDots();
    this.go(this.index, { instant: true, first: first });
  };

  Slider.prototype.buildDots = function () {
    if (!this.dotsWrap) return;
    var self = this;
    this.dotsWrap.innerHTML = '';
    this.dots = this.slides.map(function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'gm-bs__dot';
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      b.addEventListener('click', function () { self.go(i, { user: true }); });
      self.dotsWrap.appendChild(b);
      return b;
    });
  };

  Slider.prototype.go = function (i, opts) {
    opts = opts || {};
    var n = this.slides.length;
    if (!n) return;
    if (this.loop) i = (i % n + n) % n;
    else i = Math.max(0, Math.min(n - 1, i));

    var self = this;
    var prev = this.slides[this.index];
    var cur = this.slides[i];
    var changed = prev !== cur;
    this.index = i;

    // position
    if (!this.fade) {
      if (opts.instant) this.root.classList.add('is-dragging');
      this.track.style.transform = 'translate3d(' + (-i * 100) + '%, 0, 0)';
      if (opts.instant) { void this.track.offsetWidth; this.root.classList.remove('is-dragging'); }
    }

    // states
    this.allSlides.forEach(function (s) {
      var on = s === cur;
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-hidden', on ? 'false' : 'true');
      s.inert = !on;
    });
    if (this.dots) {
      this.dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        if (k === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
    if (this.prevBtn && !this.loop) this.prevBtn.disabled = i === 0;
    if (this.nextBtn && !this.loop) this.nextBtn.disabled = i === n - 1;

    this.updateAmbient(cur);
    var vpPanel = cur.querySelector('.gm-bh__vp-panel');
    if (vpPanel) fitVp(vpPanel);

    // media
    this.allSlides.forEach(function (s) { if (s !== cur) pauseSlide(s); });
    if (this.inView || opts.first) playSlide(cur);
    loadSlide(this.slides[(i + 1) % n]);

    if (changed) this.lookbooks.forEach(function (lb) { lb.closeAll(); });

    // text: hide the old slide after it has moved out, replay the new one
    if (changed && prev) {
      var t = setTimeout(function () { if (!prev.classList.contains('is-active')) hideSlideText(prev); }, this.fade ? 900 : 780);
      this.resetTimers.push(t);
    }
    if (changed || opts.first) {
      if (changed) hideSlideText(cur);
      if (this.inView) revealSlide(cur);
    }

    this.syncRotators();
    this.restart();
  };

  Slider.prototype.syncRotators = function () {
    var cur = this.slides[this.index];
    var live = this.inView && !document.hidden;
    this.rotators.forEach(function (r) { r.setActive(live && r.slide === cur); });
  };

  /* Auto background: use a small version of the slide's image */
  Slider.prototype.updateAmbient = function (slide) {
    if (!this.amb || this.amb.length < 2 || !slide) return;
    var src = '';
    var img = slide.querySelector('.gm-bh__media img, .gm-bh__col-media img, .gm-bh__iframe-poster img');
    if (img) {
      var set = img.getAttribute('srcset');
      src = set ? set.split(',')[0].trim().split(' ')[0] : (img.currentSrc || img.getAttribute('src') || '');
    }
    if (!src) {
      var v = slide.querySelector('video[poster]');
      if (v) src = v.getAttribute('poster');
    }
    if (!src || src === this.ambSrc) return;   // placeholder slide: keep the last glow
    this.ambSrc = src;
    this.ambIdx = this.ambIdx ? 0 : 1;
    var next = this.amb[this.ambIdx], prev = this.amb[this.ambIdx ? 0 : 1];
    next.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
    next.classList.add('is-on');
    prev.classList.remove('is-on');
  };

  Slider.prototype.next = function (user) { this.go(this.index + 1, { user: user }); };
  Slider.prototype.prev = function (user) { this.go(this.index - 1, { user: user }); };

  /* ---------- autoplay ---------- */
  Slider.prototype.canPlay = function () {
    return this.autoplay && this.inView && !this.userPaused && !this.editorPaused &&
      this.slides.length > 1 && !document.hidden;
  };
  Slider.prototype.restart = function () {
    clearTimeout(this.timer);
    this.timer = null;
    this.root.classList.remove('is-playing');
    if (!this.canPlay()) return;
    var cur = this.slides[this.index];
    var dur = (cur && parseInt(cur.dataset.duration, 10) * 1000) || this.speed;
    this.root.style.setProperty('--gm-speed', dur + 'ms');
    void this.root.offsetWidth; // restart the progress bar
    this.root.classList.add('is-playing');
    this.remaining = dur;
    if (this.hoverPaused) return; // wait for mouse leave
    this.startedAt = Date.now();
    this.timer = setTimeout(this.next.bind(this, false), this.remaining);
  };
  Slider.prototype.holdForHover = function (on) {
    if (!this.pauseHover) return;
    this.hoverPaused = on;
    this.root.classList.toggle('is-hover-paused', on);
    if (!this.root.classList.contains('is-playing')) return;
    if (on) {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      }
    } else if (!this.timer) {
      this.startedAt = Date.now();
      this.timer = setTimeout(this.next.bind(this, false), this.remaining);
    }
  };
  Slider.prototype.pauseByUser = function () {
    if (!this.autoplay || this.userPaused) return;
    this.setUserPaused(true);
  };
  Slider.prototype.setUserPaused = function (on) {
    this.userPaused = on;
    this.root.classList.toggle('is-user-paused', on);
    if (this.pauseBtn) {
      this.pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.pauseBtn.setAttribute('aria-label', on ? 'Play slideshow' : 'Pause slideshow');
    }
    this.restart();
  };

  /* ---------- events ---------- */
  Slider.prototype.bind = function () {
    var self = this;
    var root = this.root;

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.prev(true); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(true); });
    if (this.pauseBtn) this.pauseBtn.addEventListener('click', function () { self.setUserPaused(!self.userPaused); });

    root.addEventListener('mouseenter', function () { self.holdForHover(true); });
    root.addEventListener('mouseleave', function () { self.holdForHover(false); });

    root.addEventListener('keydown', function (e) {
      var tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.getAttribute('role') === 'tab') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.prev(true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.next(true); }
    });

    // swipe / drag
    var startX = 0, startY = 0, dx = 0, dragging = false, tracking = false, suppressClick = false;
    this.viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('.gm-bs__arrow, .gm-bs__controls, .gm-bh__tablist, .gm-bh__vp-panel, .gm-lb__dot, .gm-lb__card, .gm-lb__look, .gm-lb__look-btn, .gm-fc__nav')) return;
      if (self.slides.length < 2) return;
      tracking = true; dragging = false; dx = 0;
      startX = e.clientX; startY = e.clientY;
    });
    window.addEventListener('pointermove', function (e) {
      if (!tracking) return;
      dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          dragging = true;
          root.classList.add('is-dragging');
        } else if (Math.abs(dy) > 10) {
          tracking = false; // vertical scroll, let the page handle it
          return;
        } else {
          return;
        }
      }
      if (!self.fade) {
        self.track.style.transform = 'translate3d(calc(' + (-self.index * 100) + '% + ' + dx + 'px), 0, 0)';
      }
    }, { passive: true });
    function endDrag() {
      if (!tracking) return;
      tracking = false;
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 50);
      if (dx < -SWIPE_MIN) self.next(true);
      else if (dx > SWIPE_MIN) self.prev(true);
      else self.go(self.index);
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    this.viewport.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // only run while on screen
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var was = self.inView;
          self.inView = en.isIntersecting;
          var cur = self.slides[self.index];
          if (!cur) return;
          if (self.inView && !was) {
            playSlide(cur);
            if (!cur.querySelector('[data-gm-animate].is-visible')) revealSlide(cur);
          } else if (!self.inView) {
            pauseSlide(cur);
          }
          self.syncRotators();
          self.restart();
        });
      }, { threshold: 0.3 });
      this.io.observe(root);
    } else {
      this.inView = true;
    }

    this.onVisibility = function () {
      var cur = self.slides[self.index];
      if (document.hidden) { if (cur) pauseSlide(cur); }
      else if (self.inView && cur) playSlide(cur);
      self.syncRotators();
      self.restart();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onMq = function () {
      self.rotators.forEach(function (r) { r.layout(); });
      self.lookbooks.forEach(function (lb) { lb.layout(); });
      self.refresh(false);
    };
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', this.onMq);
    else mqMobile.addListener(this.onMq);
  };

  Slider.prototype.destroy = function () {
    clearTimeout(this.timer);
    this.resetTimers.forEach(clearTimeout);
    this.rotators.forEach(function (r) { r.setActive(false); });
    this.lookbooks.forEach(function (lb) { lb.destroy(); });
    if (this.io) this.io.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (mqMobile.removeEventListener) mqMobile.removeEventListener('change', this.onMq);
    else mqMobile.removeListener(this.onMq);
    this.root._gmSlider = null;
  };

  /* =======================================================
     Init + theme editor
     ======================================================= */
  function init(scope) {
    (scope || document).querySelectorAll('[data-gm-slider]').forEach(function (el) {
      if (!el._gmSlider) new Slider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-gm-slider]');
    if (el && el._gmSlider) el._gmSlider.destroy();
  });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.closest('[data-gm-slider]');
    if (!el || !el._gmSlider) return;
    var s = el._gmSlider;
    s.editorPaused = true;
    var i = s.slides.indexOf(e.target);
    if (i > -1) s.go(i, { user: true });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var el = e.target.closest('[data-gm-slider]');
    if (!el || !el._gmSlider) return;
    el._gmSlider.editorPaused = false;
    el._gmSlider.restart();
  });
})();
